// src/admin/pages/Categories.jsx
import React, { useState, useEffect, useMemo } from "react";
import { FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpIcon,
  PlusIcon,
  ArrowDownOnSquareStackIcon,
} from "@heroicons/react/24/outline";
import { obtenerCategorias } from "../../api";

const Categories = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [categoriesData, setCategoriesData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categories = await obtenerCategorias();
        const mapped = categories.map((c) => ({
          id: c.id,
          name: c.name || c.nombre,
          description: c.description || c.descripcion,
          imageUrl: c.image_url || c.imagen_url || "https://via.placeholder.com/300x200?text=Category",
        }));
        setCategoriesData(mapped);
      } catch (error) {
        console.error("Error loading categories:", error);
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, []);

  const filteredCategories = useMemo(() => {
    return categoriesData.filter((c) =>
      c.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, categoriesData]);

  if (loading) return <div className="p-8 text-white">Loading categories...</div>;

  return (
    <div className="flex h-full">
      {/* Sidebar */}
      <aside className="w-64 p-4 bg-[rgb(var(--color-bg))] text-[rgb(var(--color-text))] border rounded-lg">
        <div className="relative mb-6">
          <FaSearch className="absolute left-3 top-3 text-[rgb(var(--color-text))]/60" />
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 pr-3 py-2 w-full bg-hover-bg text-primary rounded"
          />
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto p-6 bg-[rgb(var(--color-bg))]">
        <div className="flex justify-between items-center mb-6 text-[rgb(var(--color-text))]">
          <button className="flex items-center space-x-1 hover:text-primary">
            <ArrowUpIcon className="h-5 w-5" />
            <span>SORT BY NAME ASCENDING</span>
          </button>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/admin/categories/create')}
              className="flex items-center space-x-1 px-3 py-1 bg-primary text-white rounded hover:bg-hover-bg"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Create</span>
            </button>
            <button className="flex items-center space-x-1 px-3 py-1 border  text-[rgb(var(--color-text))] rounded hover:border-[rgb(var(--color-text))]">
              <ArrowDownOnSquareStackIcon className="h-5 w-5" />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* Grid */}
        <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          {filteredCategories.map((cat) => (
            <div
              key={cat.id}
              onClick={() => navigate(`/admin/categories/${cat.id}`)}
              className="cursor-pointer group bg-gradient-to-br from-primary/70 to-secondary/70 border rounded-2xl shadow-xl overflow-hidden transform transition-transform duration-300 hover:scale-105"
            >
              {/* Imagen 16:9 */}
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={cat.imageUrl}
                  alt={cat.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/30"></div>
              </div>

              {/* Info */}
              <div className="p-4 text-[rgb(var(--color-text))]">
                <h3 className="text-lg font-audiowide truncate">
                  {cat.name}
                </h3>
                <div className="text-sm text-[rgb(var(--color-text))]/80 mt-1 truncate">
                  {cat.description}
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default Categories;
